import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Divider, Menu, MenuItem, Tooltip, TooltipProps, styled, tooltipClasses } from "@mui/material";
import React, { FC, MouseEvent, ReactElement, useCallback, useEffect } from 'react';
import { AppSettings } from '@/AppSettings';
import { useSessionStorage } from 'react-use';
import { grey } from '@mui/material/colors';
import { SourceDataFinding, SourceDataFindingStatus, SourceDataFindingType } from '@/api/dtos';
import { ITrialsContext } from '@/contexts/data/TrialsContext';
import { IMasterGroupsContext } from '@/contexts/data/MasterGroupsContext';
import { ICoordinatingGroupContext } from '@/contexts/data/CoordinatingGroupContext';
import { IInstitutionsContext } from '@/contexts/data/InstitutionsContext';
import { IParticipantsContext } from '@/contexts/data/ParticipantsContext';
import { ICoordinatingGroupsContext } from '@/contexts/data/CoordinatingGroupsContext';
import sourceDataFindingTypeToText from '@/utilities/configuration/sourceDataFindingTypeToText';
import sourceDataFindingStatusToText from '@/utilities/configuration/sourceDataFindingStatusToText';
import { ISearchField } from '@/components/dialogs/SearchDialog';
import Select from '@/components/forms/inputs/Select';

const SourceDataFindingsListSortTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        minWidth: theme.spacing(10),
        fontSize: '1rem',
        fontWeight: 'normal',
        padding: theme.spacing(2),
        backgroundColor: grey[700]
    },
}));

const StyledMenuItem = styled(MenuItem)(({ theme}) => ({
    gap: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
}));

const StyledDivider = styled(Divider)(({ theme}) => ({
    margin: `0 !important`
}));

export interface ISourceDataFindingsListSortableContexts {
    trials: ITrialsContext['idMap'];
    masterGroups: IMasterGroupsContext['idMap'];
    coordinatingGroups: ICoordinatingGroupsContext['idMap'];
    institutions: IInstitutionsContext['idMap'];
    participants: IParticipantsContext['idMap'];
}

export interface ISourceDataFindingsListSortable {
    sort: (itemA: SourceDataFinding, itemB: SourceDataFinding, context: ISourceDataFindingsListSortableContexts) => -1 | 0 | 1;
    name: string;
}

export interface ISourceDataFindingsListSortProps {
    sortKey: string;
    sortableFields?: ISourceDataFindingsListSortable[],
    onChange: (sort: {field: ISourceDataFindingsListSortable; direction: 'asc' | 'desc'}) => void;
}

export interface ISourceDataFindingsListFilterProps {
    selectedType?: SourceDataFindingType;
    selectedStatus?: SourceDataFindingStatus;
    onChange: (type?: SourceDataFindingType, status?: SourceDataFindingStatus) => void;
}

export const SourceDataFindingsListFilter: FC<ISourceDataFindingsListFilterProps> = ({
    selectedStatus,
    selectedType,
    onChange
}) => {
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const onTypeSelected = useCallback((type?: SourceDataFindingType) => {
        handleClose();

        let newStatus = selectedStatus;

        onChange(type, newStatus);
    }, [onChange, selectedStatus, handleClose]);

    const onStatusSelected = useCallback((status?: SourceDataFindingStatus) => {
        handleClose();

        onChange(selectedType, status);
    }, [onChange, selectedType, handleClose])

    return (
        <>
            <SourceDataFindingsListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Type</strong>
                        <Box>
                            {
                                selectedType ?
                                    sourceDataFindingTypeToText(selectedType) :
                                    'All'
                            }
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-type"
                    color="inherit"
                    aria-label="type selection"
                    aria-controls="menu-type"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth style={{ fontSize: '1rem' }} />}
                >
                    {
                        selectedType ?
                            sourceDataFindingTypeToText(selectedType) :
                            'All'
                    }
                </Button>
            </SourceDataFindingsListSortTooltip>
            <Menu
                id="menu-type"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-type'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                
                <StyledMenuItem 
                    onClick={() => onTypeSelected()}
                >
                    All
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem 
                    onClick={() => onTypeSelected(SourceDataFindingType.Information)}
                >
                    {sourceDataFindingTypeToText(SourceDataFindingType.Information)}
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem
                    onClick={() => onTypeSelected(SourceDataFindingType.ImportantProtocolDeviation)}
                >
                    {sourceDataFindingTypeToText(SourceDataFindingType.ImportantProtocolDeviation)}
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem 
                    onClick={() => onTypeSelected(SourceDataFindingType.ProtocolDeviation)}
                >
                    {sourceDataFindingTypeToText(SourceDataFindingType.ProtocolDeviation)}
                </StyledMenuItem>
            </Menu>
            <SourceDataFindingsListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Status</strong>
                        <Box>
                            {
                                selectedStatus ?
                                    sourceDataFindingStatusToText(selectedStatus) :
                                    'All'
                            }
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-status"
                    color="inherit"
                    aria-label="type selection"
                    aria-controls="menu-status"
                    aria-haspopup="true"
                    sx={{ml: 2}}
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth style={{ fontSize: '1rem' }} />}
                >
                    {
                        selectedStatus ?
                            sourceDataFindingStatusToText(selectedStatus) :
                            'All'
                    }
                </Button>
            </SourceDataFindingsListSortTooltip>
            <Menu
                id="menu-status"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-status'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                
                <StyledMenuItem 
                    onClick={() => onStatusSelected()}
                >
                    All
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem 
                    onClick={() => onStatusSelected(SourceDataFindingStatus.Pending)}
                >
                    {sourceDataFindingStatusToText(SourceDataFindingStatus.Pending)}
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem 
                    onClick={() => onStatusSelected(SourceDataFindingStatus.Resolved)}
                >
                    {sourceDataFindingStatusToText(SourceDataFindingStatus.Resolved)}
                </StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem 
                    onClick={() => onStatusSelected(SourceDataFindingStatus.NotApplicable)}
                >
                    {sourceDataFindingStatusToText(SourceDataFindingStatus.NotApplicable)}
                </StyledMenuItem>
            </Menu>
        </>
    );
}

const SourceDataFindingsListSort: FC<ISourceDataFindingsListSortProps> = ({
    sortableFields,
    sortKey,
    onChange
}) => {
    const [sortName, setSortName] = useSessionStorage<string | undefined>(`${sortKey}-sort`, undefined);
    const [sortDirection, setSortDirection] = useSessionStorage<'asc' | 'desc' | undefined>(`${sortKey}-sort-direction`, 'asc')
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);


    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const onSortSelected = useCallback((field: ISourceDataFindingsListSortable, direction: 'asc' | 'desc') => {
        setSortName(field.name);
        setSortDirection(direction);
        handleClose();

        onChange({ field, direction });
    }, [setSortDirection, setSortName, handleClose])

    useEffect(() => {
        if (!sortableFields || sortableFields.length == 0) {
            return;
        }

        let field = sortableFields.find(x => x.name === sortName) ?? sortableFields[0];

        //if (sortName !== field.name) {
            onSortSelected(field, sortDirection ?? 'asc');
        //}
    }, [sortableFields, onSortSelected, sortName, sortDirection]);

    const onFieldSelected = useCallback((field: ISourceDataFindingsListSortable) => {
        setSortName(field.name);
        handleClose();

        if (sortDirection) {
            onChange({ field, direction: sortDirection });
        }
    }, [setSortName, handleClose, sortableFields, sortDirection])

    const onDirectionSelected = useCallback((direction: 'asc' | 'desc') => {
        setSortDirection(direction);
        handleClose();

        let field = sortableFields?.find(x => x.name === sortName) ?? sortableFields?.[0];

        if (field) {
            onChange({ field, direction });
        }
    }, [setSortDirection, handleClose, sortableFields, sortName])

    if (!sortableFields || sortableFields.length == 0) {
        return null;
    }

    return (
        <>
            <SourceDataFindingsListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Field</strong>
                        <Box>
                            {sortName}
                        </Box>
                    </>
                }
            >
                <Button
                    id="btn-field"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-field"
                    aria-haspopup="true"
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                >
                    {sortName}
                </Button>
            </SourceDataFindingsListSortTooltip>
            <Menu
                id="menu-field"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-field'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                {
                    sortableFields?.map((x, i, a) => {
                        return (
                            [
                                <StyledMenuItem key={x.name} onClick={() => onFieldSelected(x)}>{x.name}</StyledMenuItem>,
                                i < (a.length - 1) ? <StyledDivider key={`${x.name}-divider`} /> : null
                            ]
                        );
                    })
                }
            </Menu>
            <SourceDataFindingsListSortTooltip
                placement="top"
                title={
                    <>
                        <strong>Sort Direction</strong>
                        {
                            sortDirection === 'desc' ? 
                                <Box>
                                    Descending <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth />
                                </Box> :
                                <Box> 
                                    Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />
                                </Box>
                        }
                    </>
                }
            >
                <Button
                    id="btn-direction"
                    color="inherit"
                    aria-label="institution selection"
                    aria-controls="menu-direction"
                    aria-haspopup="true"
                    sx={{ml: 2}}
                    onClick={handleMenu}
                    endIcon={<FontAwesomeIcon icon={faChevronDown} fixedWidth  style={{fontSize: '1rem'}} />}
                >
                    {sortDirection === 'desc' ? <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /> : <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth />}
                </Button>
            </SourceDataFindingsListSortTooltip>
            <Menu
                id="menu-direction"
                anchorEl={anchorEl}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={anchorEl?.id === 'btn-direction'}
                onClose={handleClose}
                MenuListProps={{
                    disablePadding: true
                }}
            >
                <StyledMenuItem onClick={() => onDirectionSelected('asc')}>Ascending <FontAwesomeIcon icon={AppSettings.icons.ascending} fixedWidth /></StyledMenuItem>
                <StyledDivider />
                <StyledMenuItem onClick={() => onDirectionSelected('desc')}>Descending  <FontAwesomeIcon icon={AppSettings.icons.descending} fixedWidth /></StyledMenuItem>
            </Menu>
        </>
    );
}



export const DefaultSortableFields: Record<string, ISourceDataFindingsListSortable> = {
    trial: {
        name: 'Trial',
        sort: (a, b, context) => {
            const itemA = a.trialId ? context.trials?.[a.trialId] : undefined; 
            const itemB = b.trialId ? context.trials?.[b.trialId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    masterGroup: {
        name: 'Master Group',
        sort: (a, b, context) => {
            const itemA = a.masterGroupId ? context.masterGroups?.[a.masterGroupId] : undefined; 
            const itemB = b.masterGroupId ? context.masterGroups?.[b.masterGroupId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    coordinatingGroup: {
        name: 'Coordinating Group',
        sort: (a, b, context) => {
            const itemA = a.coordinatingGroupId ? context.coordinatingGroups?.[a.coordinatingGroupId] : undefined; 
            const itemB = b.coordinatingGroupId ? context.coordinatingGroups?.[b.coordinatingGroupId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    institution: {
        name: 'Institution',
        sort: (a, b, context) => {
            const itemA = a.institutionId ? context.institutions?.[a.institutionId] : undefined; 
            const itemB = b.institutionId ? context.institutions?.[b.institutionId] : undefined;
            
            const valueA = itemA?.name ?? '';
            const valueB = itemB?.name ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    participant: {
        name: 'Participant',
        sort: (a, b, context) => {
            const itemA = a.participantId ? context.participants?.[a.participantId] : undefined; 
            const itemB = b.participantId ? context.participants?.[b.participantId] : undefined;
            
            const valueA = itemA?.studyNumber ?? '';
            const valueB = itemB?.studyNumber ?? '';

            return valueA < valueB ? -1 : 1
        }
    },
    type: {
        name: 'Type',
        sort: (a, b, context) => sourceDataFindingTypeToText(a.type!) < sourceDataFindingTypeToText(b.type!) ? -1 : 1
    },
    status: {
        name: 'Status',
        sort: (a, b, context) => sourceDataFindingStatusToText(a.status!) < sourceDataFindingStatusToText(b.status!) ? -1 : 1
    }
}



export default SourceDataFindingsListSort;